// src/components/admin/AdminSubscriptions.tsx
// ✅ GRAIA — Tabs com cache React Query (sem re-fetch ao voltar à aba)
import React, { useState, useRef } from 'react';
import './AdminSubscriptions.css';
import { LayoutDashboard, Users, Star, ShoppingBag, Pencil, UserPlus, Plus, RefreshCw } from 'lucide-react';

import SubOverview from './contas/SubOverview';
import SubUsuarios from './contas/SubUsuarios';
import SubContasVip from './contas/SubContasVip';
import SubContasVendedores from './contas/SubContasVendedores';
import SubContasEditores from './contas/SubContasEditores';

type SubTab = 'overview' | 'usuarios' | 'vip' | 'vendedores' | 'editores';
type ModalMode = 'create' | 'promote';
type ModalHandle = { openModal: (mode: ModalMode) => void };
type OverviewHandle = { refresh: () => void };

const TABS: { id: SubTab; label: string; icon: React.ElementType }[] = [
    { id: 'overview', label: 'Visão Geral', icon: LayoutDashboard },
    { id: 'usuarios', label: 'Usuários', icon: Users },
    { id: 'vip', label: 'VIP', icon: Star },
    { id: 'vendedores', label: 'Vendedores', icon: ShoppingBag },
    { id: 'editores', label: 'Editores', icon: Pencil },
];

const TAB_ACTIONS: Record<SubTab, { label: string; mode: ModalMode; secondary?: boolean }[]> = {
    overview: [],
    usuarios: [],
    vip: [
        { label: 'Promover conta', mode: 'promote', secondary: true },
        { label: 'Nova Conta VIP', mode: 'create' },
    ],
    vendedores: [
        { label: 'Promover conta', mode: 'promote', secondary: true },
        { label: 'Novo Vendedor', mode: 'create' },
    ],
    editores: [
        { label: 'Promover conta', mode: 'promote', secondary: true },
        { label: 'Novo Editor', mode: 'create' },
    ],
};

const AdminSubscriptions: React.FC = () => {
    const [activeTab, setActiveTab] = useState<SubTab>('overview');

    const ovRef = useRef<OverviewHandle>(null);
    const vipRef = useRef<ModalHandle>(null);
    const vendRef = useRef<ModalHandle>(null);
    const ediRef = useRef<ModalHandle>(null);

    const tabRefs: Record<string, React.RefObject<ModalHandle | null>> = { vip: vipRef, vendedores: vendRef, editores: ediRef };

    const actions = TAB_ACTIONS[activeTab];

    return (
        <div className="sub-container adm-page">
            <div className="adm-page-header">
                <h1 className="adm-page-title">Assinaturas & Contas</h1>
                <p className="adm-page-subtitle">Gerencie usuários, assinaturas e permissões especiais</p>
            </div>

            {/* ── Tab bar + action buttons na mesma linha ── */}
            <div className="sub-tabs-row">
                <div className="adm-tabs" style={{ marginBottom: 0 }} role="tablist">
                    {TABS.map(tab => (
                        <button
                            key={tab.id}
                            role="tab"
                            aria-selected={activeTab === tab.id}
                            className={`adm-tab ${activeTab === tab.id ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            <tab.icon size={15} />
                            <span>{tab.label}</span>
                        </button>
                    ))}
                </div>

                <div className="sub-tab-actions">
                    {activeTab === 'overview' && (
                        <button className="sub-action-btn secondary" onClick={() => ovRef.current?.refresh()}>
                            <RefreshCw size={13} /> Atualizar
                        </button>
                    )}
                    {actions.map((action) => (
                        <button
                            key={action.label}
                            className={`sub-action-btn ${action.secondary ? 'secondary' : 'primary'}`}
                            onClick={() => tabRefs[activeTab]?.current?.openModal(action.mode)}
                        >
                            {action.secondary ? <UserPlus size={13} /> : <Plus size={13} />}
                            {action.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* ── Tab content — keep-alive via hidden divs ── */}
            <div className="sub-tab-content">
                <div style={{ display: activeTab === 'overview' ? 'block' : 'none' }}><SubOverview ref={ovRef} /></div>
                <div style={{ display: activeTab === 'usuarios' ? 'block' : 'none' }}><SubUsuarios /></div>
                <div style={{ display: activeTab === 'vip' ? 'block' : 'none' }}><SubContasVip ref={vipRef} /></div>
                <div style={{ display: activeTab === 'vendedores' ? 'block' : 'none' }}><SubContasVendedores ref={vendRef} /></div>
                <div style={{ display: activeTab === 'editores' ? 'block' : 'none' }}><SubContasEditores ref={ediRef} /></div>
            </div>
        </div>
    );
};

export default AdminSubscriptions;
